import { useMemo, useState, type ReactNode } from "react";
import { Check, Search, Star } from "lucide-react";

import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";

/**
 * 品牌资产(logo / 二维码)选择器 —— 可搜索的缩略图网格(2026-08-08)。
 *
 * ═══ 为什么换掉原来那个 popover 列表 ═══════════════════════════════════════════
 * 用户报的是「**logo 太多无法选择**」。原来的选择器是一个 `max-h-64` 的纵向列表,
 * 每行一个 32px 缩略图 + 截断的名字:
 *   · 没有搜索 —— 十几个 logo 就得靠滚动一个个认;
 *   · 缩略图太小 —— 同一个品牌的深色版/浅色版/带字版在 32px 上根本分不出来;
 *   · 名字被 `truncate` 切掉 —— 而区分它们的信息恰恰在名字后半段(「…-深色」「…-无字」)。
 * 三条叠起来 = 认不出来也搜不出来,只能靠猜。
 *
 * ★所以改的是**认得出来**这件事,不只是"加个搜索框":缩略图放大到 64px 见方、
 *   名字整行不截断、默认项置顶并标出来。搜索是最后一道,不是唯一一道。
 *
 * ★空态与单项态**不在这里处理** —— 调用方比这里更清楚该说什么
 *   (「这个租户还没上传过 logo」要引导去品牌资产页,而不是显示一个空网格)。
 */
export interface BrandAssetOption {
  id: string;
  name: string;
  isDefault: boolean;
  /** 缩略图;没有就退回色块(colorHex),再没有退回灰块。 */
  signedUrl?: string | null;
  colorHex?: string | null;
  /**
   * 自绘缩略图(压过 signedUrl)。二维码的 URL 档没有图片,要现场按链接生成一张
   * (`UrlQrPreview`)—— 那种也得看得见,否则一排灰块里根本认不出哪个是哪个。
   */
  thumb?: ReactNode;
  /** 右下角小标(二维码用来分「上传 / 链接」两档)。 */
  badge?: string;
}

export function BrandAssetPicker({
  options,
  selectedId,
  onPick,
  searchPlaceholder = "搜名字…",
  emptyHint = "没有匹配的",
}: {
  options: BrandAssetOption[];
  /** 当前挂着的那个(打勾);没有就不打。 */
  selectedId?: string | null;
  onPick: (option: BrandAssetOption) => void;
  searchPlaceholder?: string;
  emptyHint?: string;
}) {
  const [q, setQ] = useState("");

  const shown = useMemo(() => {
    const kw = q.trim().toLowerCase();
    const hit = kw ? options.filter((o) => o.name.toLowerCase().includes(kw)) : options;
    // 默认项置顶 —— 大多数时候要选的就是它,不该让人在网格里找。
    return [...hit].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  }, [options, q]);

  /* 超过一屏才给搜索框:三五个的时候一个搜索框只是噪音。 */
  const showSearch = options.length > 6;

  return (
    <div className="space-y-2">
      {showSearch && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 pl-7 text-xs"
            aria-label={searchPlaceholder}
          />
        </div>
      )}

      {shown.length === 0 ? (
        <p className="py-4 text-center text-2xs text-muted-foreground">{emptyHint}</p>
      ) : (
        <div className="grid max-h-72 grid-cols-3 gap-2 overflow-y-auto pr-1">
          {shown.map((o) => {
            const picked = selectedId != null && o.id === selectedId;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => onPick(o)}
                aria-label={o.name || "未命名"}
                aria-pressed={picked}
                className={cn(
                  "group relative flex flex-col items-center gap-1 rounded border p-1.5 text-left transition",
                  picked
                    ? "border-primary bg-primary/5"
                    : "border-border/60 hover:border-primary/50 hover:bg-accent"
                )}
              >
                {/* ★64px 见方:同品牌的深色/浅色/带字版在 32px 上分不出来,而它们恰恰最常并存 */}
                <span className="flex h-16 w-full items-center justify-center overflow-hidden rounded bg-muted/40">
                  {o.thumb ? (
                    o.thumb
                  ) : o.signedUrl ? (
                    <img
                      src={o.signedUrl}
                      alt={o.name || "未命名"}
                      className="max-h-16 max-w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span
                      className="h-8 w-8 rounded"
                      style={{ backgroundColor: o.colorHex ?? "#888888" }}
                      aria-hidden
                    />
                  )}
                </span>
                {/* ★不 truncate:区分同族资产的信息就在名字后半段(「…-深色」「…-无字」) */}
                <span className="w-full break-all text-center text-2xs leading-tight">
                  {o.name || "未命名"}
                </span>
                {o.badge && (
                  <span className="absolute bottom-1 right-1 rounded bg-muted px-1 text-2xs text-muted-foreground">
                    {o.badge}
                  </span>
                )}
                {o.isDefault && (
                  <span
                    className="absolute left-1 top-1 rounded bg-primary/15 p-0.5 text-primary"
                    title="默认"
                  >
                    <Star className="h-2.5 w-2.5 fill-current" />
                  </span>
                )}
                {picked && (
                  <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                    <Check className="h-2.5 w-2.5" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
